import { useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useMainLayoutContext } from '../../../components/MainLayout'
import { DocumentModel } from '../../../defs/defs'
import { useApi } from '../../classes/eqxApi'
import EqxIcon from '../EqxIcon'
import './EqxUploadPanel.css'

interface EqxUploadPanelFile {
    preview: string
    name: string
    isImage: boolean
}

interface EqxUploadPanelProps {
    endpoint?: string;
    onSuccess?: (documents: DocumentModel[]) => void;
    onInputFiles?: (files: File[]) => void;
    singleFile?: boolean;
    accept?: string[];
}

function Previews(props: EqxUploadPanelProps) {
    const [files, setFiles] = useState<EqxUploadPanelFile[]>([])
    const [uploadError, setUploadError] = useState<string | null>(null)
    const api = useApi()
    const mainLayoutContext = useMainLayoutContext()
    const {
        addSuccessMessage: showGlobalSuccess,
        addErrorMessage: showGlobalError,
        addLoadingMessage: showGlobalLoading,
    } = mainLayoutContext.messages

    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
        accept: props.accept ?
            props.accept.reduce((acc, mimeType) => {
                acc[mimeType] = [];
                return acc;
            }, {} as Record<string, string[]>) :
            {
                'image/*': [],
            },
        multiple: !props.singleFile,
        onDrop: (acceptedFiles) => {
            // If singleFile is enabled, only take the first file
            const filesToProcess = props.singleFile ? acceptedFiles.slice(0, 1) : acceptedFiles;

            setFiles(
                filesToProcess.map((file) =>
                    Object.assign(file, {
                        isImage: file.type.startsWith('image/'),
                        preview: URL.createObjectURL(file),
                    })
                )
            )
            setUploadError(null)

            // Call onInputFiles event if provided
            if (props.onInputFiles) {
                props.onInputFiles(filesToProcess);
            }

            // Only upload if endpoint is provided
            if (props.endpoint) {
                //upload the files
                const formData = new FormData()
                filesToProcess.forEach((file) => {
                    formData.append('files', file)
                })
                //generate upload id
                const uploadId = Math.random().toString(36).substring(7)
                showGlobalLoading('Uploading files...', 'uploading-' + uploadId)
                api.postFormData(props.endpoint, formData)
                    .then((response) => {
                        console.log(response)
                        if (props.onSuccess) {
                            props.onSuccess(response.data as DocumentModel[])
                        }
                        setUploadError(null)
                        showGlobalSuccess('Files uploaded succesfully', 'uploading-' + uploadId, true)
                    })
                    .catch((error) => {
                        setUploadError(error?.message || 'An error occurred while uploading the files. Please try again.')
                        showGlobalError('Error uploading files', 'uploading-' + uploadId, true)
                    })
            } else {
                // If no endpoint, just call onSuccess with empty array to indicate files were selected
                if (props.onSuccess) {
                    props.onSuccess([])
                }
            }
        },
    })

    const removeFile = (fileName: string) => {
        setFiles(files.filter(file => file.name !== fileName));
        // Call onInputFiles with empty array to indicate no files
        if (props.onInputFiles) {
            props.onInputFiles([]);
        }
        // Call onSuccess with empty array to indicate no documents
        if (props.onSuccess) {
            props.onSuccess([]);
        }
    };

    const thumbs = files.map((file, i) => (
        <div className="eqx-upload-panel-thumb-container" key={i}>
            <div className="eqx-upload-panel-thumb" key={file.name}>
                <div className="eqx-upload-panel-thumb-inner">
                    {file.isImage ? <img
                        src={file.preview}
                        className="eqx-upload-panel-thumb-img"
                        // Revoke data uri after image is loaded
                        onLoad={() => {
                            URL.revokeObjectURL(file.preview)
                        }}
                    /> : <div className="eqx-upload-panel-thumb-file">
                        <EqxIcon fa="fas fa-file" className="eqx-upload-panel-thumb-file-icon" />
                    </div>}
                    <button
                        type="button"
                        className="eqx-upload-panel-remove-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            removeFile(file.name);
                        }}
                        title="Remove file"
                    >
                        <EqxIcon fa="fas fa-times" className="eqx-upload-panel-remove-btn-icon" />
                    </button>
                </div>
            </div>
            <div className="eqx-upload-panel-thumb-name text-truncate">{file.name}</div>
        </div>
    ))

    useEffect(() => {
        // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
        return () => files.forEach((file) => URL.revokeObjectURL(file.preview))
    }, [])

    return (
        <section
            className={
                'eqx-upload-panel ' + (isFocused ? 'focussed' : isDragAccept ? 'accept' : isDragReject ? 'reject' : '')
            }
        >
            <div {...getRootProps({ className: 'dropzone' })}>
                <input {...getInputProps()} />
                <div>
                    <EqxIcon fa="fas fa-plus-circle" /> Drag and drop some files here, or click to select files
                </div>
            </div>
            {thumbs.length > 0 && (
                <div className={'eqx-upload-panel-thumbs ' + (uploadError ? 'error' : '')}>
                    <h5>Uploaded Files</h5>
                    <div className="eqx-upload-panel-thumbs-box">
                        {uploadError && <div className="alert alert-danger">{uploadError}</div>}
                        <div className="eqx-upload-panel-thumbs-inner">
                            <div>{thumbs}</div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    )
}

export default Previews
